import type { Column, Table } from "@tanstack/react-table";
import {
  LucideBarChart2,
  Maximize2,
  Minimize2,
  RefreshCcw,
} from "lucide-react";
import React, { useEffect, useRef } from "react";
import { useIntl } from "react-intl";

import { DataTableActionsFloatingMenu } from "./DataTableActionsFloatingMenu";
import ExportManager from "./ExportManager";
import type { WpxTableFunctions } from "./types/dataTable.types";
import { wpxTableMessages } from "./wpxTable.intl";

import { ACTION_ID, SELECTOR_ID } from "@/app/constants/global-constants";
import { useDataTableSlotContext } from "@/components/dataTable/DataTableSlotContext";
import Icons from "@/components/Icons";
import Search from "@/components/search/SearchFunction";
import { Button } from "@/components/ui/button";
import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import * as DropdownMenu from "@/components/ui/DropdownMenu";
import { useFullScreenHandle } from "@/hooks/browser/useFullScreen";
import type { useQueryFilterType } from "@/hooks/query/useQueryParams";
import type { Any as AnyValue, ExtendedColumnDef } from "@/interfaces/globals";
import { cn, formatToCapitalizedWords } from "@/utils/core/generalUtils";

interface TableFunctionsProps<TData, TValue> {
  functions: WpxTableFunctions<TData, TValue> | undefined;
  hasWritePermission: boolean;
  columns: ExtendedColumnDef<TData, TValue>[];
  table: Table<TData>;
  data: TData[];
  filterOperation: useQueryFilterType;
  hide?: boolean;
  hasColumnReordered: boolean;
  resetColumnOrder: () => void;
}

/**
 * Renders the table toolbar with search, add, column toggle, export, and floating menu actions.
 *
 * @param {TableFunctionsProps<T, V>} props - Component props.
 * @returns {React.JSX.Element} Rendered table toolbar.
 */
const TableFunctionsComponent = <T, V>(
  props: TableFunctionsProps<T, V>,
): React.JSX.Element => {
  const {
    functions,
    columns,
    data,
    table,
    hasWritePermission,
    filterOperation,
    hide,
    hasColumnReordered,
    resetColumnOrder,
  } = props;
  const intl = useIntl();
  const {
    isExport,
    search,
    columnToggle,
    add,
    floatingMenu,
    floatingMenuClassName,
    floatingMenuCustom,
    multiFloating,
    hasExportInFloatingMenu,
    allowedExportOptions,
    custom,
    showRenderableFunctions = true,
    scope,
  } = functions || {};

  const { setFunctions } = useDataTableSlotContext();
  const resetSearchRef = useRef<{ resetSearch: () => void }>(null);
  const handle = useFullScreenHandle();

  const resetSearch = (): void => {
    if (resetSearchRef?.current) {
      resetSearchRef.current.resetSearch();
    }
  };

  const visibleLeafColumns = table.getVisibleLeafColumns();
  const hasActionColumn = visibleLeafColumns.some(
    (col) => col.id === ACTION_ID,
  );
  const minVisibleCount = hasActionColumn ? 3 : 2;

  const isColumnToggleable = React.useCallback(
    (column: Column<T, unknown>): boolean => {
      const isSystemColumn =
        column.id === ACTION_ID || column.id === SELECTOR_ID;
      const isHideable = column.getCanHide();
      const isExplicitlyNonHidable =
        columns.find((hide) => hide.id === column.id)?.enableHiding === false;

      return !isSystemColumn && isHideable && !isExplicitlyNonHidable;
    },
    [columns],
  );

  const toggleableColumns = React.useMemo(() => {
    return table.getAllColumns().filter((column) => isColumnToggleable(column));
  }, [table, isColumnToggleable]);

  useEffect(() => {
    if (setFunctions) {
      setFunctions({
        functions,
        columns,
        data,
        table,
        hasWritePermission,
        filterOperation,
        resetSearch,
        hasColumnReordered,
        resetColumnOrder,
      } as AnyValue);
    }

    return (): void => {
      setFunctions?.(null);
    };
  }, [
    columns,
    data,
    filterOperation,
    functions,
    hasWritePermission,
    resetColumnOrder,
    setFunctions,
    table,
    hasColumnReordered,
  ]);

  return (
    <>
      {showRenderableFunctions && !hide && (
        <div className='flex items-start gap-2'>
          {search && (
            <Search
              search={search}
              updateParams={filterOperation.updateParams}
              searchKey={functions?.searchKey || "search"}
              scope={scope}
              ref={resetSearchRef}
            />
          )}
          {custom && custom.position === "center" && custom.node}
          {hasWritePermission && add && (
            <ButtonWithIcon
              icon='add'
              variant={add?.variant}
              size={"sm"}
              className={cn(
                "text-sm font-normal dark:text-white",
                add?.className,
              )}
              disabled={add?.disabled}
              onClick={add.handleClick}
            >
              {add?.label}
            </ButtonWithIcon>
          )}
          {custom && custom.position === "left" && custom.node}
          {custom && custom.position === "right" && custom.node}
          <DropdownMenu.DropdownMenu>
            <DropdownMenu.DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm'>
                <Icons name='square-toggle' />
              </Button>
            </DropdownMenu.DropdownMenuTrigger>
            <DropdownMenu.DropdownMenuContent
              align='end'
              className='w-[200px] space-y-1'
            >
              {/* Export Option */}
              {isExport && (
                <DropdownMenu.DropdownMenuItem asChild>
                  <ExportManager
                    columns={columns}
                    data={data}
                    table={table}
                    allowedOptions={allowedExportOptions}
                  />
                </DropdownMenu.DropdownMenuItem>
              )}

              {/* Column Toggle Option */}
              {columnToggle && (
                <>
                  <DropdownMenu.DropdownMenuSub>
                    <DropdownMenu.DropdownMenuSubTrigger>
                      <LucideBarChart2 className='mr-2 h-4 w-4' />
                      <span>
                        {intl.formatMessage(wpxTableMessages.columns)}
                      </span>
                    </DropdownMenu.DropdownMenuSubTrigger>
                    <DropdownMenu.DropdownMenuSubContent className='w-64'>
                      {/* Don't allow disabling table header except for actions and select column */}
                      {toggleableColumns.map((column) => {
                        return (
                          <DropdownMenu.DropdownMenuCheckboxItem
                            key={column.id}
                            className='capitalize'
                            checked={column.getIsVisible()}
                            onCheckedChange={(value) =>
                              column.toggleVisibility(!!value)
                            }
                            onSelect={(e) => {
                              e.preventDefault();
                            }}
                            disabled={
                              column.getIsVisible() &&
                              visibleLeafColumns.length <= minVisibleCount
                            }
                          >
                            {formatToCapitalizedWords(column.id)}
                          </DropdownMenu.DropdownMenuCheckboxItem>
                        );
                      })}
                    </DropdownMenu.DropdownMenuSubContent>
                  </DropdownMenu.DropdownMenuSub>
                </>
              )}

              {/* Full Screen Toggle */}
              <DropdownMenu.DropdownMenuItem
                onClick={handle.active ? handle.exit : handle.enter}
              >
                {handle.active ? (
                  <>
                    <Minimize2 className='mr-2 h-4 w-4 rotate-180' />
                    <span>
                      {intl.formatMessage(wpxTableMessages.exitFullScreen)}
                    </span>
                    <DropdownMenu.DropdownMenuShortcut>
                      {intl.formatMessage(wpxTableMessages.escShortcut)}
                    </DropdownMenu.DropdownMenuShortcut>
                  </>
                ) : (
                  <>
                    <Maximize2 className='mr-2 h-4 w-4' />
                    <span>
                      {intl.formatMessage(wpxTableMessages.enterFullScreen)}
                    </span>
                  </>
                )}
              </DropdownMenu.DropdownMenuItem>
              {/* Reset Column Order */}
              {hasColumnReordered && (
                <DropdownMenu.DropdownMenuItem onSelect={resetColumnOrder}>
                  <RefreshCcw className='mr-2 h-4 w-4' />
                  <span>
                    {intl.formatMessage(wpxTableMessages.resetColumnOrder)}
                  </span>
                </DropdownMenu.DropdownMenuItem>
              )}
            </DropdownMenu.DropdownMenuContent>
          </DropdownMenu.DropdownMenu>
        </div>
      )}
      {floatingMenu && (
        <DataTableActionsFloatingMenu
          // children={floatingMenuCustom}
          isExport={hasExportInFloatingMenu}
          allowedExportOptions={allowedExportOptions}
          className={floatingMenuClassName}
          columns={columns}
          data={data}
          table={table}
          multiFloating={multiFloating}
        >
          {floatingMenuCustom}
        </DataTableActionsFloatingMenu>
      )}
    </>
  );
};

const TableFunctions = React.memo(
  TableFunctionsComponent,
) as typeof TableFunctionsComponent;

export default TableFunctions;
